<template>
  <AhOvDocuments
    :application="application"
    :computed-documents-groups="computedDocumentsGroups"
    :application-is-editable="applicationIsEditable"
    :rejected-categories="rejectedCategories"
    :is-private-flow="isPrivateFlow"
    :descriptions="{}"
    :documents-per-category="documentsPerCategory"
    :document-info-texts="documentInfoTexts"
    :labels="labels"
    :title="
      isPrivateFlow
        ? `Ladda upp filer för ${secondaryTenantDisplayName}`
        : 'Ladda upp filer för övertagaren'
    "
    :category-types="['transferee']"
    @goToNextCategory="goToNextCategory($router)"
  >
    <CollapseCard
      slot="collapse-card"
      is-primary
      initially-open
      class="mb-4"
      :closed-identifier="`default_info_card_${reasonType}`"
    >
      <h3 slot="title-text">Läs detta först</h3>
      <template slot="body-text">
        <div class="info-text-container">
          <p>
            För att säkerställa att rätt förutsättningar finns för att göra en
            överlåtelse kräver hyresvärden underlag från dig. Dessa
            underlagsdokument är viktiga för att kunna handlägga ditt ärende.
          </p>
        </div>
        <br />
        <p>Tänk på att:</p>
        <ul>
          <li>Alla fyra hörn skall vara synliga</li>
          <li>Kvaliteten skall vara god även vid utskrift</li>
          <li>
            Filerna måste vara i något av följande format, .pdf / .jpg / .png
          </li>
        </ul>
        <p>
          <strong>Tips:</strong> För att fota dokument på bästa sätt
          rekommenderar vi att du använder dig av en app som scannar av
          dokument. Exempel på appar som fungerar bra till både iOS och Android
          är Adobe Scan och Genius Scan, båda helt gratis att använda.
        </p>
        <ul>
          <li>
            <a
              href="https://apps.apple.com/se/app/adobe-scan-document-scanner/id1199564834"
              target="_blank"
              >Länk till Adobe Scan iOS</a
            >
          </li>
          <li>
            <a
              href="https://apps.apple.com/se/app/genius-scan-pdf-scanner/id377672876"
              target="_blank"
              >Länk till Genius Scan iOS</a
            >
          </li>
          <li>
            <a
              href="https://play.google.com/store/apps/details?id=com.adobe.scan.android&hl=sv"
              target="_blank"
              >Länk till Scan Android</a
            >
          </li>
          <li>
            <a
              href="https://play.google.com/store/apps/details?id=com.thegrizzlylabs.geniusscan.free&hl=sv"
              target="_blank"
              >Länk till Genius Scan Android</a
            >
          </li>
        </ul>
        <p>
          Behöver du hjälp? Hör av dig till oss på
          <a
            :href="`mailto:${getSupportAddress}?subject=Överlåtelser`"
            target="_blank"
          >
            {{ getSupportAddress }}</a
          >
        </p>
      </template>
    </CollapseCard></AhOvDocuments
  >
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AhOvDocuments from '@/components/AhOvDocuments.vue';
import CollapseCard from '@/components/CollapseCard.vue';

export default {
  components: {
    AhOvDocuments,
    CollapseCard
  },
  computed: {
    ...mapGetters({
      secondaryTenantDisplayName: 'ovApplication/secondaryTenantDisplayName',
      isPrivateFlow: 'ovApplication/isPrivateFlow',
      applicationIsEditable: 'ovApplication/applicationIsEditable',
      rejectedCategories: 'ovApplication/rejectedCategories',
      requiredInviteeDocuments: 'ovApplication/requiredInviteeDocuments',
      noneInviteeRequiredDocuments:
        'ovApplication/noneInviteeRequiredDocuments',
      application: 'ovApplication/application',
      getSupportAddress: 'app/getSupportAddress'
    }),
    reasonType() {
      return this.application?.reason?.type;
    },
    computedDocumentsGroups() {
      return this.application?.inviteeComputedDocumentsGroups || {};
    },
    documentsPerCategory() {
      return [
        ...this.requiredInviteeDocuments,
        ...this.noneInviteeRequiredDocuments
      ];
    },
    labels() {
      return {
        'csn-intyg': 'Här laddar du upp intyg från CSN',
        familjebevis: 'Här laddar du upp ditt familjebevis',
        fullmakt: 'Här laddar du upp fullmakt',
        övrigt: this.isPrivateFlow
          ? 'Här laddar du upp övriga dokument som styrker din anledning'
          : 'Här laddar du upp ev. övriga dokument',
        bouppteckningskopia: 'Här laddar du upp en kopia på bouppteckning',
        anställningsbevis: 'Här laddar du upp anställningsbevis',
        kontrakt: 'Här laddar du upp ditt nuvarande hyresavtal',
        uc: this.isPrivateFlow
          ? 'Här laddar du upp din UC'
          : 'Här laddar du upp UC för firmatecknarna',
        köpekontrakt: 'Här laddar du upp köpekontrakt',
        dodsfallsintyg: 'Här laddar du upp dödsfallsintyg',
        adressbevis: 'Här laddar du upp intyg om utflyttning',
        lakarintyg: 'Här laddar du upp läkarintyg som styrker hälsoproblemen',
        separationsintyg:
          'Här laddar du upp intyg om separation eller skilsmässa',
        inkomstunderlag: 'Här laddar du upp intyg om inkomst',
        aldreboendeintyg:
          'Här laddar du upp intyg från kommunen om plats på äldreboende eller liknande',
        studier: 'Här laddar du upp intyg om avslutade studier',
        antagningsbesked:
          'Här laddar du upp intyg om antagningsbesked eller registreringsintyg',
        arsredovisning: 'Här laddar du upp er årsredovisning',
        hyresavtal: 'Här laddar du ert hyresavtal',
        registreringsbevis: 'Här laddar du upp ert registreringsbevis',
        affarsplan: 'Här laddar du upp er affärsplan'
      };
    },
    documentInfoTexts() {
      if (!this.isPrivateFlow) {
        return {
          uc: [
            {
              title: 'För vem ska jag ladda upp en UC? ',
              content: `Kreditupplysningen gäller för firmatecknaren/firmatecknarna på bolaget. Du laddar enkelt ned en kreditupplysning 
                via tjänsten <a href="http://www.minuc.se/min-upplysning/" target="_blank">Min upplysning</a> för 39kr/person. Observera att kreditupplysningen måste inhämtas personligen 
                från MinUC. Inga andra kreditupplysningar godkänns.`
            }
          ],
          arsredovisning: [
            {
              title: 'Varför behövs min årsredovisning?',
              content: `Årsredovisningen behövs för att hyresvärden ska kunna bedöma er ekonomiska stabilitet och förmåga 
              att fullfölja hyresavtalet. Detta är en del av hyresvärdens bedömning inför ett eventuellt godkännande av 
              överlåtelsen`
            }
          ],
          registreringsbevis: [
            {
              title: 'Var hittar jag mitt registreringsbevis?',
              content: `Det hittar du på mina sidor hos Bolagsverket. 
              <a href="https://bolagsverket.se/omoss/varaetjanster.2187.html#:~:text=Mina%20sidor%20p%C3%A5%20bolagsverket.se&text=Du%20n%C3%A5r%20v%C3%A5ra%20e%2Dtj%C3%A4nster,registreringsbevis%20och%20%C3%A5rsredovisning%20utan%20kostnad." target="_blank">Klicka här för att komma dit.</a>`
            }
          ],
          affarsplan: [
            {
              title: 'Vad ska vara med i affärsplanen?',
              content: `Om din verksamhet är relativt nystartad så efterfrågar många hyresvärdar en affärsplan, 
                för att få förståelse för din verksamhet. En affärsplan bör innehålla en sammanfattning av din affärsidé, 
                verksamhetsplan och en ekonomisk plan.`
            }
          ],
          övrigt: [
            {
              title: 'Vad kan vara övriga dokument?',
              content: `Om du bedriver verksamhet som kräver särskilda tillstånd – till exempel inom restaurangbranschen där alkoholtillstånd krävs – behöver du även ladda upp dessa dokument. Det kan gälla serveringstillstånd, tillstånd från kommunen eller andra krav som är kopplade till lokalens användning. Säkerställ att alla nödvändiga handlingar är bifogade för att undvika förseningar i handläggningen.`
            }
          ]
        };
      }

      return {
        uc: [
          {
            title: 'Hur laddar jag upp UC?',
            content: `Du laddar enkelt ned en kreditupplysning via tjänsten <a href="http://www.minuc.se/min-upplysning/" target="_blank">Min upplysning</a> för 39kr/person. 
            Observera att kreditupplysningen måste inhämtas personligen från MinUC. 
            Inga andra kreditupplysningar godkänns.`
          }
        ],
        familjebevis: [
          {
            title:
              'Vad är ett familjebevis och varför måste jag ladda upp ett sådant?',
            content: `Ett familjebevis är en viss typ av personbevis som inte bara visar folkbokföringsadress utan även eventuella barn och andra familjeförhållanden. Familjebeviset skickas till din folkbokföringsadress via e-post och går ej att hämta online. Körkort, ID-kort samt pass gäller ej som personbevis. Beställning av familjebevis gör du enklast <a href="https://skatteverket.se/privat/folkbokforing/bestallpersonbevis/allapersonbevis.4.361dc8c15312eff6fdb20.html?mode=list&code=1209" target="_blank">här</a> (länk till Skatteverket).`
          },
          {
            title: 'För vem ska jag ladda upp familjebevis?',
            content: `Du behöver ladda upp familjebevis för nuvarande hyresgäst och föreslagen hyresgäst. Tänk på att familjebeviset får ej vara äldre än 3 månader.`
          }
        ],
        inkomstunderlag: [
          {
            title: 'Vad är ett intyg om inkomst?',
            content: `Ett intyg för inkomst kan exempelvis vara en lönespecifikation, föräldraledighet / Försäkringskassan, 
              A-kassa, CSN, pension m.m. gäller också som inkomst och då bifogas liknande underlag i form av utbetalningar, 
              beslut m.m.`
          },
          {
            title: 'Inkomstdeklaration gäller ej som inkomstunderlag.',
            content: `Som egenföretagare (Enskild firma) behövs balans- samt resultat-rapport. Som egenföretagare (Aktiebolag) 
              gäller samma som för Enskild firma och i de fall det finns en revisor bifogas med fördel även ett intyg på 
              löneutbetalningar från denne. Besparingar räknas inte som löpande inkomst.`
          }
        ]
      };
    }
  },
  methods: {
    ...mapActions({
      goToNextCategory: 'ovApplication/goToNextCategory'
    })
  }
};
</script>

<style></style>
